'use client';

import { motion } from 'framer-motion';
import { StatsSection } from '@/types/sanity';
import { SectionHeader } from './SectionHeader';
import { SectionCTAs } from './SectionCTAs';
import {
  ScrollReveal,
  StaggerContainer,
  staggerItemVariants,
} from '@/components/animations/ScrollAnimations';
import { cn } from '@/lib/utils';

interface StatsSectionComponentProps {
  section: StatsSection;
  pageSlug?: string;
}

export function StatsSectionComponent({ section, pageSlug }: StatsSectionComponentProps) {
  const layout = section.layout || 'row';

  const getGridClass = () => {
    switch (layout) {
      case 'row':
        return 'flex flex-wrap justify-center gap-12 md:gap-16';
      case 'grid-2':
        return 'grid grid-cols-1 sm:grid-cols-2 gap-8';
      case 'grid-3':
        return 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8';
      case 'grid-4':
        return 'grid grid-cols-2 lg:grid-cols-4 gap-8';
      default:
        return 'flex flex-wrap justify-center gap-12 md:gap-16';
    }
  };

  return (
    <ScrollReveal direction="up" delay={0.1}>
      <section
        className={cn(
          'stats-section py-8 md:py-12 lg:py-16 bg-muted/30',
          pageSlug && `stats-section--${pageSlug}`
        )}
      >
        <div className="mx-auto max-w-[90rem] sm:px-6 lg:px-8">
          <div className="px-6 sm:px-0">
            <SectionHeader title={section.title} subtitle={section.subtitle} />

            <StaggerContainer staggerDelay={0.1} className={getGridClass()}>
              {section.stats?.map((stat) => (
                <motion.div key={stat._key} variants={staggerItemVariants} className="text-center">
                  <p className="text-4xl md:text-5xl font-bold text-primary">
                    {stat.prefix}
                    {stat.value}
                    {stat.suffix}
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground">{stat.label}</p>
                </motion.div>
              ))}
            </StaggerContainer>

            <SectionCTAs ctas={section.ctas} />
          </div>
        </div>
      </section>
    </ScrollReveal>
  );
}
